import { ContractModelTypes } from '@mono/proto-clients';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { onTimeReading } from '../../../components/delivery/widgets/kpiReadings';
import { contractModelClient, deliveryClient } from '../../../lib/connect/clients';
// The Command Center reads two things (SAD-999), whatever the number of
// programs:
//   - getPortfolioDeliverySummary (ryugu ContractModelService) → every
//     deliverable figure, scoped server-side to the programs the viewer can
//     see: totals, per program / team / owner, the KPI readings;
//   - getPortfolioDashboard (ryugu DeliveryService) → each program's
//     projected identity and money (contract number, funded %), masked
//     server-side for a restricted viewer.
// Home's summary card reads the summary too (SAD-1005); the Evidence Board
// and the Calendar still fold the per-program reads below until they move
// onto it (design slice 3).
const KEY = 'command-center';
// 10 s ceiling on every command-center RPC. Without a deadline, a hanging
// ryugu/sabaody response keeps isLoading: true forever — the page never
// leaves its skeleton state. AbortSignal.any merges the query's own signal
// (fires on unmount/query-cancel) with the deadline so whichever fires first
// wins, and React Query transitions the query to isError.
const RPC_TIMEOUT_MS = 10000;
function withTimeout(signal) {
    return AbortSignal.any([
        signal,
        AbortSignal.timeout(RPC_TIMEOUT_MS)
    ]);
}
export function usePortfolioDashboard(filter) {
    return useQuery({
        queryKey: [
            KEY,
            'dashboard',
            filter.companyId,
            filter.sectorId,
            filter.businessUnitId,
            filter.portfolioId,
            filter.contractState
        ],
        queryFn: async ({ signal })=>{
            return deliveryClient.getPortfolioDashboard({
                companyId: filter.companyId,
                sectorId: filter.sectorId ?? '',
                businessUnitId: filter.businessUnitId ?? '',
                portfolioId: filter.portfolioId ?? '',
                contractState: filter.contractState ?? ''
            }, {
                signal: withTimeout(signal)
            });
        },
        enabled: !!filter.companyId,
        staleTime: 30000,
        retry: 1
    });
}
/** The on-time reading of a fold: overdue (state "late") dates count as misses. */ export function countsOnTime(c) {
    const overdue = c.stateCounts.late ?? 0;
    return {
        ...onTimeReading({
            onTime: c.onTime,
            timingKnown: c.timingKnown,
            overdue
        }),
        overdue
    };
}
export function usePortfolioSummary(body) {
    return useQuery({
        // The body is the whole key: the same filter on the same day is one cache entry.
        queryKey: [
            KEY,
            'summary',
            body.toJsonString()
        ],
        queryFn: async ({ signal })=>{
            const resp = await contractModelClient.getPortfolioDeliverySummary({
                body
            }, {
                signal: withTimeout(signal)
            });
            return {
                body: resp.body ?? new ContractModelTypes.GetPortfolioDeliverySummaryResponse(),
                hiddenCount: resp.hiddenCount ?? 0
            };
        },
        // A filter change keeps the last figures on screen until the new ones land.
        placeholderData: keepPreviousData,
        staleTime: 30000,
        retry: 1
    });
}
// ---------------------------------------------------------------------------
// The portfolio list (SAD-1000): the drill-down rows in one read a page.
// ---------------------------------------------------------------------------
export const LIST_PAGE_SIZE = 50;
/** The listed rows, a page at a time: "Show more" costs one more read. */ export function usePortfolioOccurrences(filter, groupBy) {
    return useInfiniteQuery({
        queryKey: [
            KEY,
            'list',
            filter.toJsonString(),
            groupBy
        ],
        initialPageParam: '',
        queryFn: async ({ pageParam, signal })=>{
            const resp = await contractModelClient.listPortfolioOccurrences({
                body: new ContractModelTypes.ListPortfolioOccurrencesRequest({
                    filter,
                    groupBy,
                    pageSize: LIST_PAGE_SIZE,
                    pageToken: pageParam
                })
            }, {
                signal: withTimeout(signal)
            });
            return {
                // A message whatever the transport answered, so every list is there.
                body: new ContractModelTypes.ListPortfolioOccurrencesResponse(resp.body ?? {}),
                hiddenCount: resp.hiddenCount ?? 0,
                programOptions: resp.programOptions ?? []
            };
        },
        getNextPageParam: (last)=>last.body.nextPageToken || undefined,
        placeholderData: keepPreviousData,
        staleTime: 30000,
        retry: 1
    });
}
// ---------------------------------------------------------------------------
// Needs Attention (SAD-1000): the ranked queue and every kind's count, one read.
// ---------------------------------------------------------------------------
/** Enough for six rows of every kind before "Show more". */ export const ATTENTION_LIMIT = 60;
export function useAttention(filter) {
    return useQuery({
        queryKey: [
            KEY,
            'attention',
            filter.toJsonString()
        ],
        queryFn: async ({ signal })=>{
            const resp = await contractModelClient.listAttentionItems({
                body: new ContractModelTypes.ListAttentionItemsRequest({
                    filter,
                    limit: ATTENTION_LIMIT
                })
            }, {
                signal: withTimeout(signal)
            });
            return new ContractModelTypes.ListAttentionItemsResponse(resp.body ?? {});
        },
        placeholderData: keepPreviousData,
        staleTime: 30000,
        retry: 1
    });
}
// ---------------------------------------------------------------------------
// Saved views (SAD-1000): the viewer's own and its tenant's shared views.
// ---------------------------------------------------------------------------
export const VIEW_PAGE = 'command_center';
const viewsKey = [
    KEY,
    'saved-views'
];
export function useSavedViews() {
    return useQuery({
        queryKey: viewsKey,
        queryFn: async ({ signal })=>{
            const resp = await contractModelClient.listSavedViews({
                body: new ContractModelTypes.ListSavedViewsRequest({
                    page: VIEW_PAGE
                })
            }, {
                signal: withTimeout(signal)
            });
            return new ContractModelTypes.ListSavedViewsResponse(resp.body ?? {}).views;
        },
        staleTime: 60000,
        retry: 1
    });
}
export function useSaveView() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (v)=>{
            const resp = await contractModelClient.saveView({
                body: new ContractModelTypes.SaveViewRequest({
                    ...v,
                    id: v.id ?? '',
                    page: VIEW_PAGE
                })
            });
            return new ContractModelTypes.SaveViewResponse(resp.body ?? {}).view;
        },
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: viewsKey
            })
    });
}
export function useDeleteSavedView() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id)=>{
            await contractModelClient.deleteSavedView({
                body: new ContractModelTypes.DeleteSavedViewRequest({
                    id
                })
            });
        },
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: viewsKey
            })
    });
}
// ---------------------------------------------------------------------------
// Remind (SAD-1000): remind a late or at-risk date's owner, as the viewer.
// The answer marks the date reminded in the queue and the list already read,
// so a reminder costs its one request and no reread.
// ---------------------------------------------------------------------------
export function useRemind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (v)=>{
            const resp = await contractModelClient.remindOccurrence({
                body: new ContractModelTypes.RemindOccurrenceRequest(v)
            });
            return new ContractModelTypes.RemindOccurrenceResponse(resp.body ?? {});
        },
        onSuccess: (r, v)=>{
            const at = r.remindedAt || new Date().toISOString();
            qc.setQueriesData({
                queryKey: [
                    KEY,
                    'attention'
                ]
            }, (old)=>old && new ContractModelTypes.ListAttentionItemsResponse({
                    ...old,
                    items: old.items.map((it)=>it.occurrenceId === v.occurrenceId && (it.kind === 'late' || it.kind === 'at_risk') ? new ContractModelTypes.PortfolioAttentionItem({
                            ...it,
                            lastRemindedAt: at
                        }) : it)
                }));
            qc.setQueriesData({
                queryKey: [
                    KEY,
                    'list'
                ]
            }, (old)=>old ? {
                    ...old,
                    pages: old.pages.map((p)=>({
                            ...p,
                            body: new ContractModelTypes.ListPortfolioOccurrencesResponse({
                                ...p.body,
                                rows: p.body.rows.map((row)=>row.occurrence?.id === v.occurrenceId ? new ContractModelTypes.PortfolioOccurrence({
                                        ...row,
                                        lastRemindedAt: at
                                    }) : row)
                            })
                        }))
                } : old);
        }
    });
}
